import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useFetchRoadDiagnosticsSegmentsQuery, useLazyFetchDefectSegmentsQuery } from "../../../../api/rtk/base/slices/diagnosticsApiSlice";
import { prepareTableData } from "../../../../features/dataFormatFunctions/dataFormatter";
import { getDataArrayWithMaintenanceEnding, getDataArrayWithMaintenancePosition } from "../../../../features/dataFormatFunctions/formatMaintenancePoint";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { setCurrentRoadDiagnosticsSegmentNew, setTableCaption, setTableDataCount, showToastMessage } from "../../../../stateManagement/reducers";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";
import LoaderWrapper from "../../../../widgets/loaderWrapper/LoaderWrapper";
import TableInfoWidget from "../../../../widgets/table/tableAddons/tableInfoWidget/TableInfoWidget";
import { prepareQueryArgsForTableFetch } from "../../../../widgets/table/tableFeatures/tableFetchFunctions";
import { configureTableLazyState, defineTableColumnsFromArray, defineTableFilters, TableEvents, tableStateModifyHandle } from "../../../../widgets/table/tableFeatures/tableFunctions";
import { getBasicTablePaginator } from "../../../../widgets/table/tableFeatures/tableTemplates/tablePaginatorTemplates";
import TableWidget from "../../../../widgets/table/tableWidget/TableWidget";
import ToastMessageContainer from "../../../../widgets/toastMessageContainer/ToastMessageContainer";
import { tableEnums } from "../../../../widgets/table/tableFeatures/tableEnums";
import { DefectsAddColumnsStructure } from "./columns";
import { DefectsHeaderColumnGroupTemplates } from "../../../../widgets/table/tableFeatures/tableTemplates/tableHeaderColumnGroupTemplates/defects";

const defectsCommonColumnsStructure: any = [
    {
        headerName: "Начало, км",
        field: "Position",
        align: "right",
        valueType: "float",
        textWrap: false
    },
    {
        headerName: "Км+",
        field: "MaintenancePosition",
        valueType: "int",
        canBeFiltered: false,
        align: "right",
        textWrap: false
    },
    {
        headerName: "Конец, км",
        field: "Ending",
        align: "right",
        valueType: "float",
        textWrap: false
    },
    {
        headerName: "Км+",
        field: "MaintenanceEnding",
        valueType: "int",
        canBeFiltered: false,
        align: "right",
        textWrap: false
    }
]

const DefectsClassNames: any = {
    4: "DefectsGOST",
    5: "DefectsODM",
}

export default function TableNestedDefects() {
    const dispatch = useDispatch()
    const { eventId, defectClassificator } = useParams();
    const [tableTitle, setTableTitle] = useState<string>("")
    const [defectsClassName, setDefectsClassName] = useState<string | null>(null)
    const [columns, setColumns] = useState<any>(null)
    const [headerColumnGroup, setHeaderColumnGroup] = useState<any>(null)
    const [tableState, setTableState] = useState<any>(null)
    const [paginator, setPaginator] = useState<any>({})
    const [paginatorPageInputTooltip, setPaginatorPageInputTooltip] = useState<any>('Введите номер страницы.')
    const [tableValue, setTableValue] = useState<any>([])
    const [tableLoading, setTableLoading] = useState<boolean>(true)

    const tableInfoWindowIsShown = useAppSelector(state => state.contentHeader.tableInfoWindowIsShown)
    const dataCount = useAppSelector(state => state.table.dataCount)

    const currentRoadDiagnosticsSegment = useAppSelector(state => state.roadDiagnosticsSegments.currentRoadDiagnosticsSegment)

    const [fetchDefects, { isError: isErrorTableValue }] = useLazyFetchDefectSegmentsQuery()

    const { data: requestedDiagnosticsSegment,
        isFetching: requestedDiagnosticsSegmentIsFetching,
        isError: requestedDiagnosticsSegmentIsError
    } = useFetchRoadDiagnosticsSegmentsQuery({ top: 1, filterString: `EventId eq ${eventId}` }, { skip: currentRoadDiagnosticsSegment })

    useEffect(() => {
        if (requestedDiagnosticsSegment?.value[0]) {
            dispatch(setCurrentRoadDiagnosticsSegmentNew(requestedDiagnosticsSegment?.value[0]))
        }
    }, [requestedDiagnosticsSegment])

    useEffect(() => {
        dispatch(setTableCaption('Дефекты'))
    }, [])

    useEffect(() => {
        if (defectClassificator) {
            const caption = tableEnums['DefectClassificator'][defectClassificator]
            const defectsClassName = DefectsClassNames?.[defectClassificator]
            setDefectsClassName(defectsClassName)
            if (caption) {
                const tableTitle = `Таблица «Дефекты» - ${caption}`
                setTableTitle(tableTitle)
            }
        }
    }, [defectClassificator])

    useEffect(() => {
        const paginatorTemplate = getBasicTablePaginator(paginatorPageInputTooltip, setPaginatorPageInputTooltip, tableState, setTableState, dataCount, defectsClassName)
        setPaginator(paginatorTemplate)
    }, [dataCount, tableState, paginatorPageInputTooltip, defectsClassName])

    useEffect(() => {
        if (defectsClassName) {
            let headerColumnGroup = DefectsHeaderColumnGroupTemplates[defectsClassName]
            setHeaderColumnGroup(headerColumnGroup)
            let columnsStructure = [...defectsCommonColumnsStructure]
            let addColumnsStructure = DefectsAddColumnsStructure?.[defectsClassName] ?? []
            columnsStructure = [...columnsStructure, ...addColumnsStructure]
            let columns = defineTableColumnsFromArray(columnsStructure)
            let filters = defineTableFilters(columns)
            setColumns(columns)
            setTableState(getItemWebStorage("tablesState")?.[defectsClassName] ?? configureTableLazyState({ filters }))
        }
    }, [defectsClassName])

    async function getTableValue() {
        setTableLoading(true)
        let { sortString, filterStringArray, page }: any = prepareQueryArgsForTableFetch(tableState, columns)
        filterStringArray.push(`DefectClassificator eq ${defectClassificator}`, `EventID eq ${eventId}`)
        const { data: defects } = await fetchDefects({ pages: true, top: 100, order: sortString, filterStringArray, skip: page * 100 })
        let value = defects?.value
        value = prepareTableData(value, columns)
        value = getDataArrayWithMaintenancePosition(value)
        value = getDataArrayWithMaintenanceEnding(value)
        setTableValue(value)
        let dataCount = defects?.['@xdata.count']
        dispatch(setTableDataCount(dataCount))
        setTableLoading(false)
    }

    useEffect(() => {
        if (columns) {
            getTableValue()
        }
    }, [columns, tableState])

    const onSort = (state: any) => tableStateModifyHandle(state, TableEvents.sort, setTableState, defectsClassName)
    const onPage = (state: any) => tableStateModifyHandle(state, TableEvents.page, setTableState, defectsClassName)
    const onFilter = (state: any) => tableStateModifyHandle(state, TableEvents.filter, setTableState, defectsClassName)

    useEffect(() => {
        if (isErrorTableValue) {
            setTableLoading(false)
        }
    }, [isErrorTableValue])

    useEffect(() => {
        if (isErrorTableValue) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }))
        }
    }, [isErrorTableValue])

    return (
        <>
            <ContentHeader title={tableTitle} type="table" option="defects" />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={tableLoading} >
                    {tableInfoWindowIsShown && <TableInfoWidget type="defects" />}
                    <TableWidget
                        tableValue={tableValue}
                        tableState={tableState}
                        headerColumnGroup={headerColumnGroup}
                        columns={columns}
                        dataCount={dataCount}
                        paginatorTemplate={paginator}
                        pageSize={100}
                        onPage={onPage}
                        onSort={onSort}
                        onFilter={onFilter}
                        isLazy={true}
                    />
                </LoaderWrapper>
            </div >
        </>
    )
}

